import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/auth";
import { PageHeader, Card, Btn, Icon, DemoBadge } from "@/components/kit";
import { toast } from "sonner";

const PLATFORMS = [
  { k: "LinkedIn", icon: "Linkedin", color: "#0A66C2" },
  { k: "Instagram", icon: "Instagram", color: "#E1306C" },
  { k: "Facebook", icon: "Facebook", color: "#1877F2" },
  { k: "X (Twitter)", icon: "Twitter", color: "#111827" },
  { k: "WhatsApp", icon: "MessageCircle", color: "#25D366" },
];

export default function Social() {
  const [connections, setConnections] = useState({});
  const [posts, setPosts] = useState([]);
  const [content, setContent] = useState("");
  const [channels, setChannels] = useState([]);
  const [scheduled, setScheduled] = useState(false);
  const [when, setWhen] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/social").then((r) => { setConnections(r.data.connections); setPosts(r.data.posts); }), []);
  useEffect(() => { load(); }, [load]);

  const toggleConn = async (p) => {
    const { data } = await api.post("/social/connections", { platform: p, connect: !connections[p] });
    setConnections(data.connections);
    toast.success(`${p} ${data.connections[p] ? "connected" : "disconnected"} (demo)`);
  };
  const toggleChannel = (p) => {
    if (!connections[p]) return toast.error(`Connect ${p} first`);
    setChannels((c) => c.includes(p) ? c.filter((x) => x !== p) : [...c, p]);
  };
  const publish = async () => {
    if (!content.trim()) return toast.error("Write something to post");
    if (channels.length === 0) return toast.error("Pick at least one connected channel");
    if (scheduled && !when) return toast.error("Pick a date & time to schedule");
    setBusy(true);
    try {
      const { data } = await api.post("/social/publish", { content, channels, schedule_at: scheduled ? when : null });
      setContent(""); setChannels([]); setScheduled(false); setWhen(""); load();
      toast.success(data.status === "scheduled" ? "Post scheduled!" : "Published to your channels!");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const del = async (id) => { setPosts((p) => p.filter((x) => x.id !== id)); await api.delete(`/social/posts/${id}`); };

  const anyConnected = Object.values(connections).some(Boolean);

  return (
    <div className="fade-up">
      <PageHeader demo={false} title="Social Studio" subtitle="Publish and schedule to LinkedIn, Instagram & more — all from one place." icon="Share2" color="#4F46E5"
        action={<span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-amber-50 text-amber-600 border border-amber-200 rounded-full px-2.5 py-1"><Icon name="Info" size={12} /> Connections are demo (no live OAuth)</span>} />

      {/* Connected accounts */}
      <Card className="mb-5">
        <h3 className="font-head font-semibold text-lg mb-3">Connected accounts</h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {PLATFORMS.map((p) => (
            <button key={p.k} onClick={() => toggleConn(p.k)} data-testid={`social-conn-${p.k.split(" ")[0].toLowerCase()}`}
              className={`rounded-2xl p-4 border text-center transition-colors ${connections[p.k] ? "border-transparent text-white" : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"}`}
              style={connections[p.k] ? { background: p.color } : {}}>
              <Icon name={p.icon} size={22} className="mx-auto mb-2" />
              <div className="text-xs font-semibold">{p.k}</div>
              <div className="text-[10px] mt-1 opacity-90">{connections[p.k] ? "Connected ✓" : "Connect"}</div>
            </button>
          ))}
        </div>
      </Card>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Composer */}
        <Card>
          <h3 className="font-head font-semibold text-lg mb-3">Compose a post</h3>
          <textarea rows={6} value={content} onChange={(e) => setContent(e.target.value)} data-testid="social-content"
            placeholder="Write your post, or paste content from the Creative Studio…"
            className="w-full rounded-xl border border-gray-200 px-3 py-3 text-sm outline-none focus:border-blue-400" />
          <div className="text-[11px] text-gray-400 mt-1 text-right">{content.length} characters</div>

          <label className="text-sm font-medium mt-3 block">Post to</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {PLATFORMS.map((p) => (
              <button key={p.k} onClick={() => toggleChannel(p.k)} data-testid={`social-channel-${p.k.split(" ")[0].toLowerCase()}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border inline-flex items-center gap-1.5 ${channels.includes(p.k) ? "text-white border-transparent" : connections[p.k] ? "border-gray-200 text-gray-600" : "border-gray-100 text-gray-300 cursor-not-allowed"}`}
                style={channels.includes(p.k) ? { background: p.color } : {}}>
                <Icon name={p.icon} size={13} /> {p.k}
              </button>
            ))}
          </div>

          <label className="flex items-center gap-2 mt-4 text-sm cursor-pointer">
            <input type="checkbox" checked={scheduled} onChange={(e) => setScheduled(e.target.checked)} data-testid="social-schedule-toggle" className="accent-blue-500" />
            Schedule for later
          </label>
          {scheduled && <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="social-schedule-when"
            className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-blue-400" />}

          <Btn onClick={publish} disabled={busy || !anyConnected} className="mt-4 w-full justify-center" data-testid="social-publish-btn">
            <Icon name={scheduled ? "Clock" : "Send"} size={16} /> {busy ? "Working…" : scheduled ? "Schedule post" : "Publish now"}
          </Btn>
          {!anyConnected && <p className="text-[11px] text-gray-400 mt-2 text-center">Connect at least one account above to start posting.</p>}
        </Card>

        {/* Feed */}
        <Card>
          <div className="flex items-center justify-between mb-3"><h3 className="font-head font-semibold text-lg">Your posts</h3><DemoBadge label="Demo — not posted to real networks" /></div>
          {posts.length === 0 ? (
            <div className="text-center py-12 text-gray-400"><Icon name="Inbox" size={30} className="mx-auto mb-2" />No posts yet. Compose your first one.</div>
          ) : (
            <div className="space-y-3">
              {posts.map((p) => (
                <div key={p.id} className="rounded-xl border border-gray-100 p-4" data-testid="social-post">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${p.status === "published" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>
                      {p.status === "published" ? "Published" : "Scheduled"}
                    </span>
                    {p.status === "scheduled" && p.schedule_at && <span className="text-[11px] text-gray-400">{new Date(p.schedule_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>}
                    <div className="ml-auto flex items-center gap-1.5">
                      {p.channels.map((ch) => { const pl = PLATFORMS.find((x) => x.k === ch); return <Icon key={ch} name={pl?.icon || "Globe"} size={14} style={{ color: pl?.color }} />; })}
                      <button onClick={() => del(p.id)} className="text-gray-300 hover:text-red-500 ml-1" data-testid="social-delete"><Icon name="Trash2" size={14} /></button>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap line-clamp-4">{p.content}</p>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
